import { useCallback, useState } from "react";
import {
  useNotify,
  usePermissions,
  useRecordContext,
  useRefresh,
  useUpdate,
  type RaRecord,
} from "react-admin";
import Box from "@mui/material/Box";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { hasAnyPermission } from "@/utils/permission-utils";
import { uploadRazaSafai } from "./upload-raza-safai";
import RazaSafaiImagePreview from "./RazaSafaiImagePreview";
import RazaSafaiUploadControls from "./RazaSafaiUploadControls";

export default function RazaSafaiShowField() {
  const record = useRecordContext<RaRecord>();
  const { permissions } = usePermissions();
  const notify = useNotify();
  const refresh = useRefresh();
  const [update, { isLoading: saving }] = useUpdate();
  const [uploading, setUploading] = useState(false);

  const isOtherMohallah = record?.isOtherMohallah === true;
  const canUpload = hasAnyPermission(permissions, ["razaRequests.edit", "razaRequests.create"]);
  const safaiUrl =
    typeof record?.safaiUrl === "string" && record.safaiUrl.trim() ? record.safaiUrl.trim() : null;
  const busy = uploading || saving;

  const persistSafaiUrl = useCallback(
    async (nextUrl: string | null) => {
      if (record?.id == null) return;
      await update(
        "razaRequests",
        {
          id: record.id,
          data: { safaiUrl: nextUrl },
          previousData: record,
        },
        { mutationMode: "pessimistic", returnPromise: true }
      );
      refresh();
    },
    [record, refresh, update]
  );

  const handleSelectFile = useCallback(
    async (file: File) => {
      setUploading(true);
      try {
        const image = await uploadRazaSafai(file);
        await persistSafaiUrl(image);
        notify("Safai uploaded", { type: "success" });
      } catch (err: unknown) {
        const message =
          (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          (err instanceof Error ? err.message : "Failed to upload safai");
        notify(message, { type: "error" });
      } finally {
        setUploading(false);
      }
    },
    [notify, persistSafaiUrl]
  );

  const handleClear = useCallback(async () => {
    try {
      await persistSafaiUrl(null);
      notify("Safai removed", { type: "info" });
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        (err instanceof Error ? err.message : "Failed to remove safai");
      notify(message, { type: "error" });
    }
  }, [notify, persistSafaiUrl]);

  if (!isOtherMohallah) {
    return null;
  }

  return (
    <Box sx={{ px: 2, pb: 2 }}>
      <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
        Safai
      </Typography>
      <Stack spacing={1}>
        {canUpload ? (
          <RazaSafaiUploadControls
            safaiUrl={safaiUrl}
            uploading={busy}
            disabled={!canUpload}
            imageVariant="show"
            onSelectFile={handleSelectFile}
            onClear={handleClear}
          />
        ) : safaiUrl ? (
          <Box sx={{ width: "100%" }}>
            <Link href={safaiUrl} target="_blank" rel="noopener noreferrer" variant="body2">
              View full image
            </Link>
            <Box
              sx={{
                mt: 1.5,
                width: { xs: "100%", sm: "85%", md: "50%" },
                maxWidth: { md: 480 },
              }}
            >
              <RazaSafaiImagePreview src={safaiUrl} variant="form" />
            </Box>
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            —
          </Typography>
        )}
      </Stack>
    </Box>
  );
}
